/**
 * Card-like dark workbench surfaces for demo apps.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../../styling/composeClassNames.js";
import { DemoSurfaceCard } from "./surfaces.js";
import { getDemoWorkbenchToneColor } from "./workbench-colors.js";

export interface DemoWorkbenchCardProps {
  readonly title?: string;
  readonly description?: React.ReactNode;
  readonly right?: React.ReactNode;
  readonly actions?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly compact?: boolean;
  readonly role?: React.AriaRole;
  readonly style?: React.CSSProperties;
}

export function DemoWorkbenchCard({
  title,
  description,
  right,
  actions,
  children,
  ariaLabel,
  className,
  compact,
  role,
  style,
}: DemoWorkbenchCardProps): React.JSX.Element {
  const cardActions = actions ?? right;
  return (
    <DemoSurfaceCard
      tone="inherit"
      className={cn("hub-panel-surface", compact ? "p-3" : "p-4", className)}
      {...(title === undefined ? {} : { title })}
      {...(description === undefined ? {} : { description })}
      {...(cardActions === undefined ? {} : { actions: cardActions })}
      {...(ariaLabel === undefined ? {} : { ariaLabel })}
      {...(role === undefined ? {} : { role })}
      {...(style === undefined ? {} : { style })}
      {...(compact === undefined ? {} : { compact })}
    >
      {children}
    </DemoSurfaceCard>
  );
}

export type DemoWorkbenchMediaCardProps = DemoWorkbenchCardProps;

export function DemoWorkbenchMediaCard({
  className,
  style,
  ...props
}: DemoWorkbenchMediaCardProps): React.JSX.Element {
  return (
    <DemoWorkbenchCard
      {...props}
      className={cn("overflow-hidden p-0", className)}
      style={{ padding: 0, overflow: "hidden", ...style }}
    />
  );
}

export type DemoWorkbenchNoticeTone = "danger" | "info" | "success" | "warning";

export interface DemoWorkbenchNoticeProps {
  readonly children: React.ReactNode;
  readonly title?: React.ReactNode;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly role?: React.AriaRole;
  readonly style?: React.CSSProperties;
  readonly tone?: DemoWorkbenchNoticeTone;
}

export function DemoWorkbenchNotice({
  children,
  title,
  ariaLabel,
  className,
  role = "note",
  style,
  tone = "info",
}: DemoWorkbenchNoticeProps): React.JSX.Element {
  const color = getDemoWorkbenchToneColor(tone === "info" ? "accent" : tone);
  return (
    <DemoSurfaceCard
      className={cn("hub-panel-surface", className)}
      role={role}
      tone="inherit"
      {...(title === undefined ? {} : { title })}
      {...(ariaLabel === undefined ? {} : { ariaLabel })}
      style={{
        background: color.bg,
        borderColor: color.border,
        color: "#cbd5e1",
        fontSize: 12,
        lineHeight: 1.65,
        ...style,
      }}
    >
      {children}
    </DemoSurfaceCard>
  );
}

export interface DemoWorkbenchInsetProps {
  readonly children?: React.ReactNode;
  readonly title?: React.ReactNode;
  readonly right?: React.ReactNode;
  readonly className?: string;
  readonly compact?: boolean;
  readonly style?: React.CSSProperties;
}

export function DemoWorkbenchInset({
  children,
  title,
  right,
  className,
  compact = false,
  style,
}: DemoWorkbenchInsetProps): React.JSX.Element {
  return (
    <div
      className={cn("hub-panel-surface-muted", className)}
      style={{
        borderRadius: 8,
        padding: compact ? 10 : 12,
        ...style,
      }}
    >
      {title !== undefined || right !== undefined ? (
        <div
          style={{
            alignItems: "center",
            display: "flex",
            gap: 8,
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          {title !== undefined ? (
            <div style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700 }}>{title}</div>
          ) : null}
          {right !== undefined ? <div>{right}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}
